import {
  addCreatedOrders,
  dispatchOrders,
  fetchOrderDetails,
  finishOrderThunk,
  readyToPickupOrderThunk,
} from "../actions/ordersSlice";
import {
  addToQueue,
  failProcessing,
  finishProcessing,
  processQueuedOrder,
  startProcessing,
} from "../actions/orderQueueSlice";
import { createListenerMiddleware } from "@reduxjs/toolkit";

export const fetchOrderDetailsMiddleware =
  ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    if (
      action.type === dispatchOrders.fulfilled.toString() ||
      action.type === finishOrderThunk.fulfilled.toString() ||
      action.type === readyToPickupOrderThunk.fulfilled.toString()
    ) {
      if (!action.payload) {
        return;
      }

      const state = getState();
      const orderId = action.payload.id;

      if (
        state.orders.showOrderInfosModal &&
        state.orders.orderInfosModalOrderId === orderId
      ) {
        dispatch(fetchOrderDetails({ orderId }));
      }
    }
    return next(action);
  };

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;
const orderRetryCounters = new Map();

export const orderQueueMiddleware = (store) => (next) => (action) => {
  const result = next(action);

  // Helper function to process next order
  const processNextOrder = async () => {
    const state = store.getState().orderQueue;

    if (state.queue.length > 0 && !state.isProcessing) {
      const nextOrder = state.queue[0];
      const orderKey = nextOrder.order_id;
      store.dispatch(startProcessing());

      try {
        await store.dispatch(processQueuedOrder(nextOrder)).unwrap();
        orderRetryCounters.delete(orderKey);
        store.dispatch(finishProcessing());

        // Check if there are more orders to process
        const updatedState = store.getState().orderQueue;
        if (updatedState.queue.length > 0 && !updatedState.isProcessing) {
          processNextOrder();
        }
      } catch (error) {
        const retries = (orderRetryCounters.get(orderKey) || 0) + 1;
        orderRetryCounters.set(orderKey, retries);

        if (retries < MAX_RETRIES) {
          // Keep order in queue and retry after delay
          store.dispatch(failProcessing());
          console.warn(
            `Order ${orderKey} failed (attempt ${retries}/${MAX_RETRIES}), retrying...`
          );
          setTimeout(() => processNextOrder(), RETRY_DELAY_MS);
        } else {
          // Max retries reached — remove from queue and move on
          console.error(
            `Order ${orderKey} failed after ${MAX_RETRIES} attempts, removing from queue.`
          );
          orderRetryCounters.delete(orderKey);
          store.dispatch(finishProcessing());

          const updatedState = store.getState().orderQueue;
          if (updatedState.queue.length > 0 && !updatedState.isProcessing) {
            processNextOrder();
          }
        }
      }
    }
  };

  // Only trigger queue processing for specific actions
  if (
    action.type === addToQueue.type &&
    !store.getState().orderQueue.isProcessing
  ) {
    processNextOrder();
  }

  return result;
};

export const listenerMiddleware = createListenerMiddleware();
listenerMiddleware.startListening({
  actionCreator: addCreatedOrders.fulfilled,
  effect: (action, listenerApi) => {
    const { orders, should_print_auto_accept, autoAcceptOrders } =
      action.payload;
    if (autoAcceptOrders) {
      orders.forEach((order) =>
        listenerApi.dispatch(addToQueue({ ...order, should_print_auto_accept }))
      );
    }
  },
});
